import { Form as AntForm, Checkbox, DatePicker, Input, InputNumber, Radio, Switch, TreeSelect } from "antd";
import React, { FC, useMemo } from "react";
import BaseDatePicker from "./fields/BaseDatePicker";
import BaseSelect from "./fields/BaseSelect";
import DynamicFormList from "./fields/DynamicFormList";
import { FormItemType } from "./consts";
import DraggerUploader from "../uploader/DraggerUploader";
import ButtonUploader from "../uploader/ButtonUploader";

interface FormItemProps {
  item: any
  index?: any
}

const FormItem: FC<FormItemProps> = ({
  item,
  index
}) => {
  const { formItemClassName, formItemStyle, renderFormItem, ...itemProps } = item

  const renderItemType = useMemo(() => {
    if (renderFormItem) {
      return renderFormItem(itemProps)
    }

    switch (itemProps.type) {
      case FormItemType.Input:
        return (
          <Input {...itemProps} />
        )
      case FormItemType.Textarea:
        return (
          <Input.TextArea {...itemProps} />
        )
      case FormItemType.Select:
        return (
          <BaseSelect
            name={itemProps.key}
            {...itemProps}
          />
        )
      case FormItemType.TreeSelect: {
        const { options, ...treeSelectProps } = itemProps
        return (
          <TreeSelect
            treeData={options}
            allowClear
            treeDefaultExpandAll
            showSearch
            treeNodeFilterProp="title"
            {...treeSelectProps}
          />
        )
      }
      case FormItemType.InputNumber:
        return (
          <InputNumber {...itemProps} />
        )
      case FormItemType.Switch:
        return (
          <Switch {...itemProps} />
        )
      case FormItemType.Checkbox:
        return (
          <Checkbox {...itemProps} />
        )
      case FormItemType.CheckboxGroup:
        return (
          <Checkbox.Group>
            {itemProps.options.map((option: any) => (
              <Checkbox value={option.value}>{option.label}</Checkbox>
            ))}
          </Checkbox.Group>
        )
      case FormItemType.Radio:
        return (
          <Radio {...itemProps} />
        )
      case FormItemType.RadioGroup:
        return (
          <Radio.Group>
            {itemProps.options.map((option: any) => (
              <Radio value={option.value}>{option.label}</Radio>
            ))}
          </Radio.Group>
        )
      case FormItemType.DatePicker:
        return (
          <BaseDatePicker
            name={itemProps.key}
            {...itemProps}
          />
        )
      case FormItemType.DateRangePicker:
        return (
          <DatePicker.RangePicker {...itemProps} />
        )
      case FormItemType.Upload:
        return (
          <ButtonUploader {...itemProps} />
        )
      case FormItemType.File:
        return (
          <DraggerUploader {...itemProps} multiple={false} />
        )
      case FormItemType.DynamicList:
        return (
          <DynamicFormList
            name={itemProps.key}
            label={itemProps.label}
            itemConfig={itemProps.itemConfig}
            addButtonText={itemProps.addButtonText}
            minItems={itemProps.minItems}
            maxItems={itemProps.maxItems}
            initialValue={itemProps.initialValue}
          />
        )
      default:
        return <p>该组件暂未支持</p>
    }
  }, [itemProps, renderFormItem])

  // Add normFile function for handling upload data
  const normFile = (e: any) => {
    if (Array.isArray(e)) {
      return e;
    }
    return e?.fileList;
  };

  // 动态表单项不需要额外的 Form.Item 包装
  if (itemProps.type === FormItemType.DynamicList) {
    return renderItemType;
  }

  return (
    <AntForm.Item
      {...itemProps}
      className={formItemClassName}
      style={formItemStyle}
      key={(typeof itemProps.key === 'object' ? itemProps.key.join(',') : itemProps.key) || index}
      name={itemProps.key}
      valuePropName={itemProps.valuePropName || (itemProps.type === FormItemType.Upload || itemProps.type === FormItemType.File ? "fileList" : undefined)}
      getValueFromEvent={itemProps.type === FormItemType.Upload || itemProps.type === FormItemType.File ? normFile : undefined}
    >
      {renderItemType}
    </AntForm.Item>
  )
}

export default FormItem
